import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { 
  RefreshCw, 
  LogOut, 
  Package, 
  TrendingUp,
  Settings,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react'
import apiService from '../services/api'
import ImageStats from '../components/ImageStats'
import AdminLayout from '../components/AdminLayout'
import toast from 'react-hot-toast'

const AdminFixed = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    start_date: '',
    end_date: ''
  })
  const [selectedReservation, setSelectedReservation] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Vérifier l'authentification (token unifié + rôle admin)
  useEffect(() => {
    const token = localStorage.getItem('user_token') || localStorage.getItem('admin_token')
    if (!token) {
      setIsLoading(false)
      navigate('/admin/login')
      return
    }
    apiService.get('/auth/me')
      .then((res) => {
        const user = res.data?.user || res.data?.admin || null
        const isAdmin = user?.role === 'admin'
        setIsAuthenticated(Boolean(isAdmin))
        setIsLoading(false)
        if (!isAdmin) navigate('/admin/login')
      })
      .catch(() => {
        setIsLoading(false)
        setIsAuthenticated(false)
        navigate('/admin/login')
      })
  }, [navigate])

  // Hooks déclarés avant tout return conditionnel
  const { data: reservationsData, isLoading: reservationsLoading, error } = useQuery(
    ['reservations', filters],
    () => apiService.get('/reservations', { params: filters }).then(r => r.data),
    {
      enabled: isAuthenticated,
      refetchInterval: 30000,
      retry: 1,
      refetchOnWindowFocus: false,
      onError: (err) => {
        console.error('Erreur lors du chargement des réservations:', err)
      }
    }
  )

  // Récupérer stats DB (produits, catégories, clients)
  const { data: dbStats } = useQuery(
    'admin-db-stats',
    () => apiService.get('/rentman/database-stats').then(r => r.data),
    { enabled: isAuthenticated, refetchOnWindowFocus: false }
  )

  const updateStatusMutation = useMutation(
    ({ id, status }) => apiService.updateReservationStatus(id, status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('reservations')
        toast.success('Statut mis à jour')
        setSelectedReservation(null)
      },
      onError: (error) => {
        toast.error('Erreur lors de la mise à jour')
        console.error('Update error:', error)
      }
    }
  )

  const deleteMutation = useMutation(
    (id) => apiService.deleteReservation(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('reservations')
        toast.success('Réservation supprimée')
        setShowDeleteModal(false)
        setSelectedReservation(null)
      },
      onError: (error) => {
        toast.error('Erreur lors de la suppression')
        console.error('Delete error:', error)
      }
    }
  )

  // Afficher un écran de chargement pendant la vérification d'authentification
  if (isLoading) {
    console.log('🔄 Affichage de l\'écran de chargement')
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-orange-500" />
          <p className="text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    )
  }

  // Rediriger si pas authentifié
  if (!isAuthenticated) {
    console.log('🚫 Utilisateur non authentifié')
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Non authentifié. Redirection vers la page de connexion...</p>
          <a href="/admin/login" className="bg-orange-500 text-white px-4 py-2 rounded">Se connecter</a>
        </div>
      </div>
    )
  }

  console.log('🎉 Rendu du dashboard admin')

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/admin/login')
    toast.success('Déconnexion réussie')
  }

  const handleStatusUpdate = (id, status) => {
    updateStatusMutation.mutate({ id, status })
  }

  const handleDelete = (id) => {
    setSelectedReservation(id)
    setShowDeleteModal(true)
  }

  const confirmDelete = () => {
    if (selectedReservation) {
      deleteMutation.mutate(selectedReservation)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'text-green-600 bg-green-100'
      case 'demande_envoyee':
        return 'text-orange-600 bg-orange-100'
      case 'cancelled':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-400 bg-gray-100'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="h-4 w-4" />
      case 'demande_envoyee':
        return <Clock className="h-4 w-4" />
      case 'cancelled':
        return <XCircle className="h-4 w-4" />
      default:
        return <Clock className="h-4 w-4" />
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmée'
      case 'demande_envoyee':
        return 'Demande envoyée'
      case 'cancelled':
        return 'Annulée'
      case 'completed':
        return 'Terminée'
      default:
        return status
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR')
  }

  // Calculer les statistiques
  console.log('🔍 isAuthenticated:', isAuthenticated)
  console.log('🔍 reservationsData:', reservationsData)
  console.log('🔍 reservationsLoading:', reservationsLoading)
  console.log('🔍 error:', error)
  const reservations = reservationsData?.reservations || []
  console.log('🔍 reservations:', reservations)
  const totalReservations = reservations.length
  const confirmedReservations = reservations.filter(r => r.status === 'confirmed').length
  const pendingReservations = reservations.filter(r => r.status === 'demande_envoyee').length
  const totalRevenue = reservations.reduce((sum, r) => sum + parseFloat(r.total_price || 0), 0)
  const averageRevenue = totalReservations > 0 ? totalRevenue / totalReservations : 0

  return (
    <AdminLayout title="Administration" subtitle="Dashboard">
      <div>
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Produits */}
          <div className="card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Package className="h-5 w-5 mr-2 text-orange-500" />
                Produits
              </h3>
              <span className="text-2xl font-bold text-orange-600">
                {dbStats?.data?.products?.total || 0}
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Disponibles</span>
                <span className="font-medium text-green-600">{dbStats?.data?.products?.available || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Avec images</span>
                <span className="font-medium text-blue-600">{dbStats?.data?.products?.with_images || 0}</span>
              </div>
            </div>
          </div>

          {/* Catégories */}
          <div className="card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Settings className="h-5 w-5 mr-2 text-orange-500" />
                Catégories
              </h3>
              <span className="text-2xl font-bold text-orange-600">
                {dbStats?.data?.categories?.total || 0}
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Principales</span>
                <span className="font-medium text-blue-600">{dbStats?.data?.categories?.root_categories || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Sous-catégories</span>
                <span className="font-medium text-purple-600">{dbStats?.data?.categories?.sub_categories || 0}</span>
              </div>
            </div>
          </div>

          {/* Clients */}
          <div className="card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">Clients</h3>
              <span className="text-2xl font-bold text-orange-600">
                {dbStats?.data?.users?.customers || 0}
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Admins</span>
                <span className="font-medium text-gray-700">{dbStats?.data?.users?.admins || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Utilisateurs</span>
                <span className="font-medium text-gray-700">{dbStats?.data?.users?.total || 0}</span>
              </div>
              <div className="pt-2">
                <a href="/admin/users" className="text-sm text-orange-600 hover:text-orange-700 underline">Voir la liste</a>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{totalReservations}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Confirmées</p>
                <p className="text-2xl font-bold text-gray-900">{confirmedReservations}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">En attente</p>
                <p className="text-2xl font-bold text-gray-900">{pendingReservations}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Revenus</p>
                <p className="text-2xl font-bold text-gray-900">{formatPrice(totalRevenue)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Statistiques des images */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <ImageStats />
        </div>

        {/* Liste des réservations déplacée dans Commandes */}

      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer cette réservation ? Cette action est irréversible.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                disabled={deleteMutation.isLoading}
              >
                {deleteMutation.isLoading ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminFixed
